import { useCallback } from 'react';

import { enqueueSnackbar } from 'notistack';

import { type BankDetails } from 'types/invoice';
import callAzureFunction from 'utils-ts/call-azure-function';
import callAzureFunctionPublic from 'utils-ts/call-azure-function-public';
import handleError from 'utils-ts/handle-error';
import { useSelector } from 'react-redux';
import { appConfigPaths, type Invoice, type PathNode } from '@a1/shared';
import { type AppConfig } from 'types/appConfig';

export interface InvoicePdfData {
  bankDetails: BankDetails | null;
  blob: Blob;
  url: string;
}

const useInvoicePdf = () => {
  const pdfHeaderImgUrl = useSelector((state: AppConfig) => state?.appConfig?.data?.images?.pdfHeader);

  const getBankDetails = useCallback(async () => {
    let bankDetails: BankDetails | null = null;

    try {
      const paymentsConfig = appConfigPaths.payments as PathNode;

      const { data } = await callAzureFunctionPublic<{ configs: { payments: BankDetails } }>({
        url: 'public/app-configurations',
        method: 'get',
        params: {
          keys: [
            (paymentsConfig.bankAccountName as PathNode).path,
            (paymentsConfig.bankAccountBSB as PathNode).path,
            (paymentsConfig.bankAccountNumber as PathNode).path,
            (paymentsConfig.companyName as PathNode).path,
            (paymentsConfig.companyAddress1 as PathNode).path,
            (paymentsConfig.companyAddress2 as PathNode).path,
            (paymentsConfig.australianBusinessNumber as PathNode).path,
            (paymentsConfig.referenceNumberHelpText as PathNode).path,
            (paymentsConfig.remittanceHelpText as PathNode).path,
            (paymentsConfig.queriesHelpText as PathNode).path,
            (paymentsConfig.phone as PathNode).path,
            (paymentsConfig.website as PathNode).path
          ],
          includeLocalStorageFalse: true
        }
      });
      bankDetails = data.configs.payments;
    } catch (error) {
      const message = handleError(error);
      enqueueSnackbar({
        variant: 'error',
        message,
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    }

    return bankDetails;
  }, []);

  const generateInvoicePdf = async ({
    invoiceId,
    invoiceData,
    filename
  }: {
    invoiceId?: string;
    invoiceData?: Invoice;
    filename?: string;
  }) => {
    const bankDetails = await getBankDetails();

    const data = {
      bankDetails,
      pdfHeaderImgUrl,
      invoiceId, // this will be ignored in the backend if `invoiceData` is provided
      invoiceData
    };

    const res = await callAzureFunction<Blob>({
      url: 'invoice-pdf-generation',
      method: 'POST',
      responseType: 'blob',
      data
    });

    // Create a URL for the blob
    const url = window.URL.createObjectURL(new Blob([res.data]));

    // Create a temporary <a> element to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || 'report.pdf'); // filename
    document.body.appendChild(link);
    link.click();
  };

  const getInvoicePdfData = useCallback(
    async ({
      invoiceId,
      invoiceData,
      invoiceToken
    }: {
      invoiceId?: string;
      invoiceData?: Invoice | null;
      invoiceToken?: string;
    }): Promise<InvoicePdfData> => {
      const bankDetails = await getBankDetails();

      const data = {
        // filename: `invoice-${invoice.invoiceNumber}.pdf`,
        bankDetails,
        pdfHeaderImgUrl,
        invoiceId, // this will be ignored in the backend if `invoiceData` is provided
        invoiceData
      };

      let pdf;

      if (invoiceToken) {
        const res = await callAzureFunctionPublic<Blob>({
          url: 'public/invoice-pdf-generation',
          method: 'POST',
          responseType: 'blob',
          data,
          headers: { Authorization: `Bearer ${invoiceToken}` }
        });
        pdf = res.data;
      } else {
        const res = await callAzureFunction<Blob>({
          url: 'invoice-pdf-generation',
          method: 'POST',
          responseType: 'blob',
          data
        });
        pdf = res.data;
      }

      const blob = new Blob([pdf], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      return {
        blob,
        url,
        bankDetails
      };
    },
    [getBankDetails, pdfHeaderImgUrl]
  );

  return { getBankDetails, generateInvoicePdf, getInvoicePdfData };
};

export default useInvoicePdf;
